import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage/MediaServerLazyImage'
import { useBookingDraftQuery } from '../../domain/hooks'
import { CheckoutSection } from '../CheckoutSection'
import styles from './Accommodation.module.css'
import { AccommodationMap } from './AccommodationMap/AccommodationMap'

const IMAGE_SRCSET_WIDTHS = [343, 686, 418, 835]

function formatTripDates(startDate: Date, endDate: Date, duration: string | undefined, locale: string) {
  const startOptions: Intl.DateTimeFormatOptions = { day: 'numeric' }
   if (startDate.getMonth() !== endDate.getMonth() || startDate.getFullYear() !== endDate.getFullYear())
     startOptions.month = 'short'
   if (startDate.getFullYear() !== endDate.getFullYear())
     startOptions.year = 'numeric'
   const start = startDate.toLocaleDateString(locale, startOptions)
  const end = endDate.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
  const range = `${start} - ${end}`
  return duration ? `${range} - ${duration}` : range
}

function Accommodation() {
  const intl = useIntl()
  const { data } = useBookingDraftQuery()

  const accommodation = data?.confirmation?.accommodation
  const accommodationName = accommodation?.name
  if (!accommodationName) {
    return null
  }

  const { imageUrl, address, url, geoLocation, tripDates, rooms } = accommodation
  const { startDate, endDate, duration } = tripDates ?? {}

  return (
    <CheckoutSection glyph="Bed" title={intl.formatMessage({ id: 'checkout_confirmation_accommodation_title' })}>
      <div className={styles.layout}>
        <MediaServerLazyImage
          className={styles.image}
          src={imageUrl ?? ''}
          widths={IMAGE_SRCSET_WIDTHS}
          alt={accommodationName}
        />
        <div className={styles.details}>
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              <Typography as="h3" variant="display-h-regular" className={styles.name}>
                {accommodationName}
              </Typography>
              {address && geoLocation && (
                <AccommodationMap
                  lat={geoLocation.latitude}
                  lng={geoLocation.longitude}
                  title={accommodationName}
                >
                  <button type="button" className={styles.address}>
                    <Icon glyph="MapPin" size={16} />
                    <Typography as="span" variant="body-s-regular-tight" className={styles.addressText}>
                      {address}
                    </Typography>
                  </button>
                </AccommodationMap>
              )}
            </div>
            {url && (
              <Button
                as="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                variant="secondary"
                size="S"
                className={styles.viewButton}
              >
                {intl.formatMessage({ id: 'checkout_confirmation_accommodation_view_button' })}
              </Button>
            )}
          </div>
          <div className={styles.rows}>
            {startDate && endDate && (
              <AccommodationRow
                glyph="Calendar"
                label={intl.formatMessage({ id: 'checkout_steps_basket_trip_dates_label' })}
              >
                {formatTripDates(startDate, endDate, duration, intl.locale)}
              </AccommodationRow>
            )}
            {!!rooms?.length && (
              <AccommodationRow glyph="Bed" label={intl.formatMessage({ id: 'checkout_steps_basket_room_label' })}>
                {rooms.map((roomName, index) => (
                  <div key={index}>{roomName}</div>
                ))}
              </AccommodationRow>
            )}
          </div>
        </div>
      </div>
    </CheckoutSection>
  )
}

function AccommodationRow({ glyph, label, children }: React.PropsWithChildren<{ glyph: IconGlyph, label: string }>) {
  return (
    <div className={styles.row}>
      <Icon glyph={glyph} size={24} />
      <div className={styles.rowContent}>
        <Typography variant="body-s-regular-tight" className={styles.rowLabel}>
          {label}
        </Typography>
        <Typography variant="body-m-bold-tight" className={styles.rowValue} as="div">
          {children}
        </Typography>
      </div>
    </div>
  )
}

export default Accommodation
